'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, TextArea } from '@common/components';
import { useToast } from '@common/hooks';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { AuthQueryOptions } from '@/api';
import { MultiFileUpload } from '@/app/(private)/my/(local-market)/orders/_components/MultiFileUpload';
import { RateInput } from '@/app/donation/(private)/my/review/_components/RateInput';
import { Image } from '@/components/client';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { IMAGE_PLACEHOLDER } from '@/constants';
import { useSubscribedState } from '@/hooks/useSubscribedState';
interface LocalMarketReviewEditModalProps {
  reviewId: string | null;
  onClose: () => void;
  onSubmit: (reviewId: string, data: {
    rating: number;
    contents: string;
    fileUrls?: string[];
  }) => void;
  isSubmitting?: boolean;
}
export const LocalMarketReviewEditModal = t0 => {
  const $ = _c(32);
  const {
    reviewId,
    onClose,
    onSubmit,
    isSubmitting
  } = t0;
  const {
    toast
  } = useToast();
  let t1;
  if ($[0] !== reviewId) {
    t1 = AuthQueryOptions["get:/wello/v2/auth/local-market/my/reviews/{reviewId}"]({
      path: {
        reviewId: reviewId ?? ""
      }
    });
    $[0] = reviewId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !!reviewId;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      ...t1,
      enabled: t2,
      select: _temp
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: reviewData,
    isLoading
  } = useQuery(t3);
  const [rating, setRating] = useSubscribedState(reviewData?.rating ?? 0);
  const [contents, setContents] = useSubscribedState(reviewData?.contents ?? "");
  let t4;
  if ($[5] !== reviewData?.fileUrls) {
    t4 = reviewData?.fileUrls?.filter(_temp2).map(_temp3).filter(Boolean) ?? [];
    $[5] = reviewData?.fileUrls;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== reviewData) {
    t5 = [reviewData];
    $[7] = reviewData;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const [fileUrls, setFileUrls] = useSubscribedState(t4, t5);
  let t6;
  if ($[9] !== contents || $[10] !== fileUrls || $[11] !== onSubmit || $[12] !== rating || $[13] !== reviewId || $[14] !== toast) {
    t6 = () => {
      if (rating < 1) {
        toast({
          type: "fail",
          message: "\uBCC4\uC810\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694."
        });
        return;
      }
      if (contents.trim().length < 10) {
        toast({
          type: "fail",
          message: "\uD6C4\uAE30\uB294 \uCD5C\uC18C 10\uAE00\uC790 \uC774\uC0C1 \uC791\uC131\uD574 \uC8FC\uC138\uC694."
        });
        return;
      }
      if (!reviewId) {
        return;
      }
      onSubmit(reviewId, {
        rating,
        contents,
        fileUrls: fileUrls.length > 0 ? fileUrls : undefined
      });
    };
    $[9] = contents;
    $[10] = fileUrls;
    $[11] = onSubmit;
    $[12] = rating;
    $[13] = reviewId;
    $[14] = toast;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleSubmit = t6;
  const t7 = !!reviewId;
  let t8;
  if ($[16] !== contents || $[17] !== fileUrls || $[18] !== handleSubmit || $[19] !== isLoading || $[20] !== isSubmitting || $[21] !== onClose || $[22] !== rating || $[23] !== reviewData || $[24] !== setContents || $[25] !== setFileUrls || $[26] !== setRating) {
    t8 = isLoading ? <div className="flex h-200 items-center justify-center"><p className="text-Gray-550 typo-body3">불러오는 중...</p></div> : reviewData ? <div className="flex grow flex-col"><div className="desktop:px-0 desktop:pt-0 flex gap-16 px-20 pb-24 pt-16"><Image src={reviewData.productImageUrl ?? IMAGE_PLACEHOLDER} alt={reviewData.productName ?? ""} width={104} height={104} className="size-104 shrink-0 rounded-large border border-Gray-300 object-cover" /><div className="flex min-w-0 flex-col gap-4"><p className="line-clamp-2 font-500 text-Gray-900 typo-body4">{reviewData.productName}</p></div></div><div className="desktop:hidden h-8 bg-Gray-100" /><div className={cn("flex flex-col gap-32 pb-24", "mobile-tablet:grow mobile-tablet:px-20 mobile-tablet:pt-32")}><div><p className="mb-8 font-400 text-Gray-900 typo-body4">상품 만족도는 어떤가요?</p><RateInput value={rating} onChange={setRating} /></div><div className="flex flex-col gap-4"><TextArea placeholder={"\uB0B4\uC6A9\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694."} value={contents} onChange={e => setContents(e.target.value)} className="h-240 w-full border-Gray-300" /></div><MultiFileUpload fileUrls={fileUrls} onChange={setFileUrls} s3PathType="LOCAL_MARKET_PRODUCT_REVIEW" maxFiles={5} maxBytes={10485760} guidelines={["\uC774\uBBF8\uC9C0\uB294 \uB2F9 10MB \uC774\uB0B4\uC758 \uC774\uBBF8\uC9C0 \uD30C\uC77C(jpg, jpeg,gif,png)\uB9CC \uCCA8\uBD80\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.", "\uC774\uBBF8\uC9C0\uB294 \uCD5C\uB300 5\uC7A5\uAE4C\uC9C0 \uCCA8\uBD80 \uAC00\uB2A5\uD569\uB2C8\uB2E4."]} /><div><div className="border-t border-Gray-200" /><p className={cn("mt-20 text-center font-400 text-Gray-600 typo-body4", "mobile-tablet:mt-16 mobile-tablet:text-left")}>{"\uC791\uC131\uD558\uC2E0 \uD6C4\uAE30\uB294 \uACE0\uAC1D\uC13C\uD130 > \uB098\uC758 \uB2F5\uB840\uD488 \uD6C4\uAE30\uC5D0\uC11C \uD655\uC778\uD558\uC2E4 \uC218 \uC788\uC5B4\uC694."}</p></div></div><footer className={cn("flex items-center justify-center gap-8 bg-Gray-white px-20 py-12", "mobile-tablet:sticky mobile-tablet:bottom-0 mobile-tablet:mt-auto mobile-tablet:shadow-lg")}><Button theme="sub-line" className="basis-[236px] mobile-tablet:flex-1" onClick={onClose}>취소</Button><Button theme="primary-fill" className="basis-[236px] mobile-tablet:flex-1" onClick={handleSubmit} loading={isSubmitting}>저장</Button></footer></div> : null;
    $[16] = contents;
    $[17] = fileUrls;
    $[18] = handleSubmit;
    $[19] = isLoading;
    $[20] = isSubmitting;
    $[21] = onClose;
    $[22] = rating;
    $[23] = reviewData;
    $[24] = setContents;
    $[25] = setFileUrls;
    $[26] = setRating;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== onClose || $[29] !== t7 || $[30] !== t8) {
    t9 = <ResponsiveDialog opened={t7} onClose={onClose} title={"\uD6C4\uAE30 \uC218\uC815"} drawerType="full" isAppBarHeader={true}>{t8}</ResponsiveDialog>;
    $[28] = onClose;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  return t9;
};
function _temp(data) {
  return data.context;
}
function _temp2(file) {
  return file.type === "IMAGE";
}
function _temp3(file_0) {
  return file_0.url ?? "";
}